<template>
  <div>
    <admin-nav active="reservation" />
    <b-container fluid>
      <b-row class="mb-1">
        <b-col>
          <h4 class="mb-0">
            Parcours de réservation
          </h4>
          <p class="text-muted small mb-0">
            Ce que le parcours public lit réellement, marque par marque.
          </p>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-select
            v-model="societeChoisie"
            :options="optionsSocietes"
            @change="charger"
          />
        </b-col>
      </b-row>

      <!-- La règle de propriété des données, écrite là où on serait tenté de
           modifier quelque chose. Nenuphar pousse ce référentiel par
           /maj/* : une saisie faite ici serait écrasée à la poussée
           suivante, sans avertissement. -->
      <b-alert
        variant="secondary"
        show
        class="small"
      >
        <strong>Lecture seule.</strong> Ces données sont poussées par le
        backend de gestion, qui en est le maître : elles se paramètrent
        là-bas. Les modifier ici serait sans effet durable.
      </b-alert>

      <b-alert
        v-if="societe && societe.refManquante"
        variant="danger"
        show
      >
        <strong>Référence Nenuphar manquante.</strong>
        La colonne <code>C_REF</code> de cette société est vide : le parcours
        <strong>ne peut choisir aucun box</strong>, car c'est elle qui signe
        les appels à Nenuphar. Toute réservation échouera jusqu'à ce qu'elle
        soit renseignée.
      </b-alert>

      <div
        v-if="chargement"
        class="text-center my-3"
      >
        <b-spinner small />
      </div>

      <template v-else>
        <b-card
          v-for="taille in tailles"
          :key="taille.ui"
          :title="taille.libelle"
          class="mb-1"
        >
          <p
            v-if="taille.memo"
            class="text-muted small"
          >
            {{ taille.memo }}
          </p>

          <div
            v-for="sst in taille.sousTailles"
            :key="sst.ui"
            class="mb-1"
          >
            <h6 class="mb-50">
              {{ sst.libelle }}
            </h6>
            <b-table-simple
              small
              responsive
              class="mb-0"
            >
              <b-thead>
                <b-tr>
                  <b-th>Tarif</b-th>
                  <b-th class="text-right">
                    HT
                  </b-th>
                  <b-th class="text-right">
                    TTC
                  </b-th>
                  <b-th class="text-right">
                    Arrhes
                  </b-th>
                  <b-th class="text-center">
                    Box libres
                  </b-th>
                  <b-th />
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="t in sst.tarifs"
                  :key="t.ui"
                  :class="t.masque ? 'text-muted' : ''"
                >
                  <b-td>
                    {{ t.libelle }}
                    <div
                      v-if="t.aide"
                      class="small text-muted"
                    >
                      {{ t.aide }}
                    </div>
                  </b-td>
                  <b-td class="text-right">
                    {{ euros(t.tarifHT) }}
                  </b-td>
                  <b-td class="text-right">
                    {{ euros(t.tarifHT * TVA) }}
                  </b-td>
                  <b-td class="text-right">
                    {{ euros(t.arrhe) }}
                  </b-td>
                  <b-td class="text-center">
                    <b-badge :variant="t.boxLibres > 0 ? 'light-success' : 'light-danger'">
                      {{ t.boxLibres }} / {{ t.boxTotal }}
                    </b-badge>
                  </b-td>
                  <b-td>
                    <b-badge
                      v-if="t.masque"
                      variant="light-secondary"
                    >
                      masqué
                    </b-badge>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </b-card>

        <b-alert
          v-if="!tailles.length"
          variant="warning"
          show
        >
          Aucune taille de box pour cette marque. Le parcours public
          n'affichera rien.
        </b-alert>

        <b-row>
          <b-col md="6">
            <b-card
              title="Assurances"
              class="mb-1"
            >
              <b-table-simple
                v-if="assurances.length"
                small
                class="mb-0"
              >
                <b-tbody>
                  <b-tr
                    v-for="a in assurances"
                    :key="a.ui"
                  >
                    <b-td>
                      {{ a.libelle }}
                      <b-badge
                        v-if="a.defaut"
                        variant="light-primary"
                        class="ml-50"
                      >
                        par défaut
                      </b-badge>
                    </b-td>
                    <b-td class="text-right">
                      {{ euros(a.tarif) }}
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mb-0"
              >
                Aucune assurance pour cette marque.
              </p>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card
              title="Motifs"
              class="mb-1"
            >
              <b-table-simple
                v-if="motifs.length"
                small
                class="mb-0"
              >
                <b-tbody>
                  <b-tr
                    v-for="m in motifs"
                    :key="m.ui"
                  >
                    <b-td>
                      {{ m.libelle }}
                      <b-badge
                        v-if="m.defaut"
                        variant="light-primary"
                        class="ml-50"
                      >
                        par défaut
                      </b-badge>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
              <p
                v-else
                class="text-muted small mb-0"
              >
                Aucun motif pour cette marque.
              </p>
            </b-card>
          </b-col>
        </b-row>
      </template>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BAlert,
  BBadge,
  BSpinner,
  BFormSelect,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
} from 'bootstrap-vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    AdminNav,
    BContainer,
    BRow,
    BCol,
    BCard,
    BAlert,
    BBadge,
    BSpinner,
    BFormSelect,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },
  data() {
    return {
      // TVA La Réunion. Le parcours public applique le même facteur, en dur
      // lui aussi : afficher un TTC calculé autrement induirait en erreur.
      TVA: 1.085,
      societes: [],
      societeChoisie: null,
      societe: null,
      tailles: [],
      assurances: [],
      motifs: [],
      chargement: false,
    }
  },
  computed: {
    optionsSocietes() {
      return this.societes.map(s => ({
        value: s.ui,
        text: s.refManquante ? `${s.libelle} — sans référence` : `${s.libelle} (${s.ref})`,
      }))
    },
  },
  created() {
    this.chargerSocietes()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },

    euros(valeur) {
      const n = Number(valeur)
      if (Number.isNaN(n)) return '—'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(n)
    },

    chargerSocietes() {
      this.$http.get('/resaadmin/societes', this.authHeaders())
        .then(response => {
          const { data } = response
          if (data.res !== 1) return
          this.societes = data.societes || []
          if (this.societes.length) {
            const [premiere] = this.societes
            this.societeChoisie = premiere.ui
            this.charger()
          }
        })
        .catch(() => {})
    },

    charger() {
      if (!this.societeChoisie) return
      this.chargement = true
      this.$http.get(`/resaadmin/referentiel/${this.societeChoisie}`, this.authHeaders())
        .then(response => {
          const { data } = response
          if (data.res !== 1) return
          this.societe = data.societe || null
          this.tailles = data.tailles || []
          this.assurances = data.assurances || []
          this.motifs = data.motifs || []
        })
        .catch(() => {
          this.tailles = []
          this.assurances = []
          this.motifs = []
        })
        .finally(() => {
          this.chargement = false
        })
    },
  },
}
</script>
